// src/pages/TikpagExperience/TikpagExperiencePins.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Edit3,
  Image,
  Medal,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  X
} from "lucide-react";

import axios from "../../axiosConfig";
import "./TikpagExperiencePins.css";

const FORM_INICIAL = {
  id: null,
  campanha_id: "",
  nome: "",
  codigo: "",
  descricao: "",
  valor_minimo: "",
  ordem: 1,
  peso_colecao: 1,
  imagem_url: "",
  icone: "",
  cor: "",
  ativo: true
};

const formatarDinheiro = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

export default function TikpagExperiencePins() {
  const [campanhas, setCampanhas] = useState([]);
  const [campanhaId, setCampanhaId] = useState("");
  const [pins, setPins] = useState([]);
  const [busca, setBusca] = useState("");
  const [formulario, setFormulario] =
    useState(FORM_INICIAL);
  const [modalAberto, setModalAberto] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] =
    useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const carregarCampanhas = useCallback(async () => {
    try {
      const resposta = await axios.get(
        "/admin/experience/campanhas"
      );

      const lista = Array.isArray(
        resposta.data?.campanhas
      )
        ? resposta.data.campanhas
        : [];

      setCampanhas(lista);

      setCampanhaId((atual) => {
        if (atual) return atual;

        return lista.length
          ? String(lista[0].id)
          : "";
      });
    } catch (err) {
      console.error(
        "Erro ao carregar campanhas para PINs:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível carregar as campanhas."
      );
    }
  }, []);

  const carregarPins = useCallback(
    async (idCampanha) => {
      if (!idCampanha) {
        setPins([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErro("");

      try {
        const resposta = await axios.get(
          `/admin/experience/campanhas/${idCampanha}/pins`
        );

        setPins(
          Array.isArray(resposta.data?.pins)
            ? resposta.data.pins
            : []
        );
      } catch (err) {
        console.error(
          "Erro ao carregar PINs Experience:",
          err
        );

        setErro(
          err.response?.data?.error ||
            "Não foi possível carregar os PINs."
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    carregarCampanhas();
  }, [carregarCampanhas]);

  useEffect(() => {
    carregarPins(campanhaId);
  }, [campanhaId, carregarPins]);

  const pinsFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    if (!termo) return pins;

    return pins.filter((pin) =>
      [
        pin.nome,
        pin.codigo,
        pin.descricao
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(termo)
    );
  }, [pins, busca]);

  const campanhaSelecionada =
    campanhas.find(
      (campanha) =>
        String(campanha.id) ===
        String(campanhaId)
    ) || null;

  const abrirNovoPin = () => {
    if (!campanhaId) {
      setErro(
        "Selecione uma campanha antes de cadastrar o PIN."
      );
      return;
    }

    setFormulario({
      ...FORM_INICIAL,
      campanha_id: Number(campanhaId)
    });

    setErro("");
    setSucesso("");
    setModalAberto(true);
  };

  const abrirEdicao = (pin) => {
    setFormulario({
      id: Number(pin.id),
      campanha_id: Number(pin.campanha_id),
      nome: pin.nome || "",
      codigo: pin.codigo || "",
      descricao: pin.descricao || "",
      valor_minimo:
        pin.valor_minimo ?? "",
      ordem:
        Number(pin.ordem || 1),
      peso_colecao:
        Number(pin.peso_colecao || 1),
      imagem_url:
        pin.imagem_url || "",
      icone: pin.icone || "",
      cor: pin.cor || "",
      ativo: Boolean(pin.ativo)
    });

    setErro("");
    setSucesso("");
    setModalAberto(true);
  };

  const fecharModal = () => {
    if (salvando) return;

    setModalAberto(false);
    setFormulario(FORM_INICIAL);
  };

  const atualizarCampo = (
    campo,
    valor
  ) => {
    setFormulario((atual) => ({
      ...atual,
      [campo]: valor
    }));
  };

  const salvarPin = async (event) => {
    event.preventDefault();

    if (!formulario.nome.trim()) {
      setErro("Informe o nome do PIN.");
      return;
    }

    if (
      formulario.valor_minimo === "" ||
      Number(formulario.valor_minimo) < 0
    ) {
      setErro(
        "Informe um valor mínimo válido."
      );
      return;
    }

    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const payload = {
        campanha_id:
          Number(formulario.campanha_id),
        nome:
          formulario.nome.trim(),
        codigo:
          formulario.codigo.trim(),
        descricao:
          formulario.descricao.trim(),
        valor_minimo:
          Number(
            formulario.valor_minimo
          ),
        ordem:
          Number(formulario.ordem),
        peso_colecao:
          Number(
            formulario.peso_colecao
          ),
        imagem_url:
          formulario.imagem_url.trim(),
        icone:
          formulario.icone.trim(),
        cor:
          formulario.cor.trim(),
        ativo:
          Boolean(formulario.ativo)
      };

      if (formulario.id) {
        await axios.put(
          `/admin/experience/pins/${formulario.id}`,
          payload
        );

        setSucesso(
          "PIN atualizado com sucesso."
        );
      } else {
        await axios.post(
          "/admin/experience/pins",
          payload
        );

        setSucesso(
          "PIN criado com sucesso."
        );
      }

      setModalAberto(false);
      setFormulario(FORM_INICIAL);

      await carregarPins(campanhaId);
    } catch (err) {
      console.error(
        "Erro ao salvar PIN Experience:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível salvar o PIN."
      );
    } finally {
      setSalvando(false);
    }
  };

  const excluirPin = async (pin) => {
    const confirmou =
      window.confirm(
        `Excluir o PIN "${pin.nome}"?`
      );

    if (!confirmou) return;

    setErro("");
    setSucesso("");

    try {
      await axios.delete(
        `/admin/experience/pins/${pin.id}`
      );

      setSucesso(
        "PIN excluído com sucesso."
      );

      await carregarPins(campanhaId);
    } catch (err) {
      console.error(
        "Erro ao excluir PIN Experience:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível excluir o PIN."
      );
    }
  };

  const totalExperiencias =
    pins.reduce(
      (total, pin) =>
        total +
        Number(
          pin.quantidade_experiencias || 0
        ),
      0
    );

  const totalConquistas =
    pins.reduce(
      (total, pin) =>
        total +
        Number(
          pin.quantidade_conquistas || 0
        ),
      0
    );

  return (
    <div className="experiencePins-page">
      <header className="experiencePins-header">
        <div>
          <span className="experiencePins-kicker">
            Tikpag Experience
          </span>

          <h1>PINs</h1>

          <p>
            Gerencie as metas mensais, pesos da
            coleção, imagens e disponibilidade dos
            PINs.
          </p>
        </div>

        <button
          type="button"
          className="experiencePins-primaryButton"
          onClick={abrirNovoPin}
        >
          <Plus size={16} />
          Novo PIN
        </button>
      </header>

      {erro && (
        <div className="experiencePins-alert erro">
          {erro}
        </div>
      )}

      {sucesso && (
        <div className="experiencePins-alert sucesso">
          {sucesso}
        </div>
      )}

      <section className="experiencePins-summary">
        <article className="experiencePins-summaryCard">
          <div className="experiencePins-summaryIcon">
            <Medal size={20} />
          </div>

          <div>
            <span>PINs cadastrados</span>
            <strong>{pins.length}</strong>
          </div>
        </article>

        <article className="experiencePins-summaryCard">
          <div className="experiencePins-summaryIcon">
            <Image size={20} />
          </div>

          <div>
            <span>
              Experiências vinculadas
            </span>

            <strong>
              {totalExperiencias}
            </strong>
          </div>
        </article>

        <article className="experiencePins-summaryCard">
          <div className="experiencePins-summaryIcon">
            <Medal size={20} />
          </div>

          <div>
            <span>Conquistas emitidas</span>
            <strong>{totalConquistas}</strong>
          </div>
        </article>
      </section>

      <section className="experiencePins-panel">
        <div className="experiencePins-toolbar">
          <div className="experiencePins-search">
            <Search size={16} />

            <input
              value={busca}
              onChange={(event) =>
                setBusca(event.target.value)
              }
              placeholder="Buscar PIN por nome ou código"
            />
          </div>

          <div className="experiencePins-filters">
            <select
              value={campanhaId}
              onChange={(event) =>
                setCampanhaId(
                  event.target.value
                )
              }
            >
              {!campanhas.length && (
                <option value="">
                  Nenhuma campanha
                </option>
              )}

              {campanhas.map(
                (campanha) => (
                  <option
                    key={campanha.id}
                    value={campanha.id}
                  >
                    {campanha.nome}
                  </option>
                )
              )}
            </select>

            <button
              type="button"
              className="experiencePins-secondaryButton"
              onClick={() =>
                carregarPins(campanhaId)
              }
              disabled={
                loading || !campanhaId
              }
            >
              <RefreshCcw
                size={16}
                className={
                  loading
                    ? "experiencePins-spin"
                    : ""
                }
              />

              Atualizar
            </button>
          </div>
        </div>

        {campanhaSelecionada && (
          <div className="experiencePins-campaignInfo">
            <div>
              <span>
                Campanha selecionada
              </span>

              <strong>
                {
                  campanhaSelecionada.nome
                }
              </strong>
            </div>

            <span
              className={`experiencePins-status ${
                campanhaSelecionada.ativa
                  ? "ativo"
                  : "inativo"
              }`}
            >
              {campanhaSelecionada.ativa
                ? "Ativa"
                : "Inativa"}
            </span>
          </div>
        )}

        <div className="experiencePins-grid">
          {pinsFiltrados.map((pin) => (
            <article
              className="experiencePins-card"
              key={pin.id}
            >
              <div className="experiencePins-cardTop">
                <div className="experiencePins-pinVisual">
                  {pin.imagem_url ? (
                    <img
                      src={pin.imagem_url}
                      alt={pin.nome}
                    />
                  ) : (
                    <Medal size={25} />
                  )}
                </div>

                <span
                  className={`experiencePins-status ${
                    pin.ativo
                      ? "ativo"
                      : "inativo"
                  }`}
                >
                  {pin.ativo
                    ? "Ativo"
                    : "Inativo"}
                </span>
              </div>

              <div className="experiencePins-cardBody">
                <span className="experiencePins-code">
                  {pin.codigo}
                </span>

                <h2>{pin.nome}</h2>

                <p>
                  {pin.descricao ||
                    "Sem descrição cadastrada."}
                </p>

                <strong className="experiencePins-value">
                  {formatarDinheiro(
                    pin.valor_minimo
                  )}
                </strong>

                <div className="experiencePins-metrics">
                  <div>
                    <span>Ordem</span>

                    <strong>
                      {Number(
                        pin.ordem || 0
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Peso</span>

                    <strong>
                      {Number(
                        pin.peso_colecao || 0
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Experiências</span>

                    <strong>
                      {Number(
                        pin.quantidade_experiencias ||
                          0
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Conquistas</span>

                    <strong>
                      {Number(
                        pin.quantidade_conquistas ||
                          0
                      )}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="experiencePins-cardActions">
                <button
                  type="button"
                  className="experiencePins-editButton"
                  onClick={() =>
                    abrirEdicao(pin)
                  }
                >
                  <Edit3 size={15} />
                  Editar
                </button>

                <button
                  type="button"
                  className="experiencePins-deleteButton"
                  onClick={() =>
                    excluirPin(pin)
                  }
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </article>
          ))}

          {!loading &&
            !pinsFiltrados.length && (
              <div className="experiencePins-empty">
                Nenhum PIN encontrado.
              </div>
            )}

          {loading && (
            <div className="experiencePins-empty">
              Carregando PINs...
            </div>
          )}
        </div>
      </section>

      {modalAberto && (
        <div
          className="experiencePins-modalOverlay"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              fecharModal();
            }
          }}
        >
          <form
            className="experiencePins-modal"
            onSubmit={salvarPin}
          >
            <div className="experiencePins-modalHeader">
              <div>
                <span className="experiencePins-kicker">
                  Tikpag Experience
                </span>

                <h2>
                  {formulario.id
                    ? "Editar PIN"
                    : "Novo PIN"}
                </h2>
              </div>

              <button
                type="button"
                className="experiencePins-closeButton"
                onClick={fecharModal}
              >
                <X size={19} />
              </button>
            </div>

            <div className="experiencePins-modalBody">
              <div className="experiencePins-formGrid">
                <label className="experiencePins-field">
                  <span>Nome do PIN</span>

                  <input
                    value={
                      formulario.nome
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "nome",
                        event.target.value
                      )
                    }
                    placeholder="Ex.: Rocket"
                    autoFocus
                  />
                </label>

                <label className="experiencePins-field">
                  <span>Código</span>

                  <input
                    value={
                      formulario.codigo
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "codigo",
                        event.target.value
                      )
                    }
                    placeholder="ROCKET"
                  />
                </label>

                <label className="experiencePins-field">
                  <span>
                    Valor mínimo
                  </span>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      formulario.valor_minimo
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "valor_minimo",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experiencePins-field">
                  <span>Ordem</span>

                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={
                      formulario.ordem
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "ordem",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experiencePins-field">
                  <span>
                    Peso na coleção
                  </span>

                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={
                      formulario.peso_colecao
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "peso_colecao",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experiencePins-field">
                  <span>Cor</span>

                  <input
                    value={
                      formulario.cor
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "cor",
                        event.target.value
                      )
                    }
                    placeholder="#7c3aed"
                  />
                </label>

                <label className="experiencePins-field">
                  <span>Ícone</span>

                  <input
                    value={
                      formulario.icone
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "icone",
                        event.target.value
                      )
                    }
                    placeholder="rocket"
                  />
                </label>

                <label className="experiencePins-field">
                  <span>
                    URL da imagem
                  </span>

                  <input
                    value={
                      formulario.imagem_url
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "imagem_url",
                        event.target.value
                      )
                    }
                    placeholder="https://..."
                  />
                </label>

                <label className="experiencePins-field full">
                  <span>Descrição</span>

                  <textarea
                    value={
                      formulario.descricao
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "descricao",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experiencePins-check full">
                  <input
                    type="checkbox"
                    checked={
                      formulario.ativo
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "ativo",
                        event.target.checked
                      )
                    }
                  />

                  <span>
                    <strong>PIN ativo</strong>

                    <small>
                      Apenas PINs ativos
                      participam das novas
                      apurações.
                    </small>
                  </span>
                </label>
              </div>
            </div>

            <div className="experiencePins-modalFooter">
              <button
                type="button"
                className="experiencePins-secondaryButton"
                onClick={fecharModal}
                disabled={salvando}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="experiencePins-primaryButton"
                disabled={salvando}
              >
                {salvando ? (
                  <RefreshCcw
                    size={16}
                    className="experiencePins-spin"
                  />
                ) : (
                  <Medal size={16} />
                )}

                {formulario.id
                  ? "Salvar alterações"
                  : "Criar PIN"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
